// LabReportForm.tsx
import React, { useState } from "react";
import api from "../../../api/axiosConfig";
import "../../../center admin css/centerAdmin.css";
import { useS3Uploader } from "../../../hooks/useS3Uploader";

interface FormComponentProps {
  emrId: string;
  onSuccess?: () => void;
}

type CollapseMap = { [key: string]: boolean };
interface LabFormData {
  [key: string]: string | number;
}

const makeEmptyFormData = (): LabFormData => ({
  hemoglobin: "",
  hematocritPct: "",
  rbc: "",
  wbc: "",
  platelet: "",
  mcv: "",
  mch: "",
  mchc: "",
  rdw: "",
  absNeutrophil: "",
  absLymphocyte: "",
  absEosinophil: "",
  absMonocyte: "",
  absBasophil: "",
  neutrophils: "",
  lymphocytes: "",
  monocytes: "",
  eosinophils: "",
  basophils: "",
  mentzerIndex: "",
  sehgalIndex: "",
  plateletHematocrit: "",
  meanPlateletVolume: "",
  neutrophilLymphocyteRatio: "",
  crp: "",
  hba1c: "",
  avgBloodGlucose: "",
  immunoglobulinE: "",
  iron: "",
  tibc: "",
  uibc: "",
  serumTransferrinSaturation: "",
  ferritin: "",
  hdl: "",
  cholesterol_total: "",
  triglycerides: "",
  vldl: "",
  cholesterol_hdl: "",
  ldl_hdl: "",
  ldl_calculated: "",
  non_hdl: "",
  hdl_ldl_ratio: "",
  alkalinePhosphatase: "",
  sgot_ast: "",
  sgpt_alt: "",
  ggt: "",
  totalBilirubin: "",
  directBilirubin: "",
  indirectBilirubin: "",
  totalProtein: "",
  albumin: "",
  globulin: "",
  ag_ratio: "",
  sgot_sgpt_ratio: "",
  tsh: "",
  t3: "",
  t4: "",
  vitaminB12: "",
  folic_acid: "",
  totalVitaminD: "",
  urea: "",
  bun: "",
  creatinine: "",
  calcium: "",
  uricAcid: "",
  egfr: "",
  bun_creatinine_ratio: "",
});

const defaultCollapse: CollapseMap = {
  cbc: false,
  iron: true,
  vitamins: true,
  lipid: true,
  thyroid: true,
  diabetes: true,
  immunoglobulin: true,
  liver: true,
  kidney: true,
  crp: true,
};

// const LabReportForm: React.FC<FormComponentProps> = ({ emrId, onSuccess }) => {
const LabReportForm: React.FC<FormComponentProps> = ({ emrId, onSuccess }) => {
  const pid = emrId;

  const sessionId =
    sessionStorage.getItem("workflowSessionId") ||
    sessionStorage.getItem("sessionId") ||
    "";

  // const sessionId = sessionStorage.getItem("workflowSessionId");

  const [formByPatient, setFormByPatient] = useState<
    Record<string, LabFormData>
  >({});
  const [ackByPatient, setAckByPatient] = useState<
    Record<string, "Yes" | "No">
  >({});
  const [collapseByPatient, setCollapseByPatient] = useState<
    Record<string, CollapseMap>
  >({});
  const [loading, setLoading] = useState(false);

  const [errors, setErrors] = useState<Record<string, boolean>>({});

  // NEW: S3 uploader hook
  const { uploadPdf, uploading: s3Uploading } = useS3Uploader(emrId);

  // MULTIPLE FILE SUPPORT
  const [labReports, setLabReports] = useState<File[]>([]);

  const formData = formByPatient[pid] ?? makeEmptyFormData();
  const acknowledged = ackByPatient[pid] ?? "No";
  const collapsed = collapseByPatient[pid] ?? defaultCollapse;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormByPatient((prev) => ({
      ...prev,
      [pid]: { ...(prev[pid] ?? makeEmptyFormData()), [name]: value },
    }));
  };

  const toggle = (key: string) => {
    setCollapseByPatient((prev) => ({
      ...prev,
      [pid]: { ...(prev[pid] ?? defaultCollapse), [key]: !collapsed[key] },
    }));
  };

  // MULTIPLE FILES PICK
  const handleFilePick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const pdfFiles = files.filter((f) => f.type === "application/pdf");
    if (pdfFiles.length > 0) {
      setLabReports((prev) => [...prev, ...pdfFiles]);
    }
  };

  const handleRemoveFile = (index: number) => {
    setLabReports((prev) => prev.filter((_, i) => i !== index));
  };

  const validateForm = () => {
    const requiredFields = [
      "hemoglobin",
      "hematocritPct",
      "rbc",
      "wbc",
      "platelet",
    ];

    const newErrors: Record<string, boolean> = {};

    requiredFields.forEach((field) => {
      if (!String(formData[field] || "").trim()) {
        newErrors[field] = true;
      }
    });

    if (acknowledged !== "Yes") {
      alert("Please confirm Lab Report Received.");
      return false;
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      alert("Please fill all required fields.");
      return false;
    }

    return true;
  };

  // -----------------------
  // SUBMIT WITH S3 UPLOAD
  // -----------------------

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);

    try {
      // 1️⃣ Upload PDFs to S3
      const uploadedUrls: string[] = [];
      for (const file of labReports) {
        const result = await uploadPdf(file, "lab-reports/official");
        if (result?.key) uploadedUrls.push(result.key);
      }

      // 2️⃣ Format payload
      const payload = {
        emrId: String(emrId),
        reportReceived: acknowledged === "Yes",

        "Complete Blood Count (CBC)": {
          "Red blood cell count (RBC count)": formData.rbc,
          "Hb - Haemoglobin": formData.hemoglobin,
          Haematocrit: formData.hematocritPct,
          MCV: formData.mcv,
          MCH: formData.mch,
          MCHC: formData.mchc,
          "Red cell distribution width (RDW)": formData.rdw,
          "Total WBC count": formData.wbc,
          "Absolute Neutrophil Count": formData.absNeutrophil,
          "Absolute Lymphocyte Count (ALC)": formData.absLymphocyte,
          "AEC - Absolute Eosinophil Count": formData.absEosinophil,
          "Absolute Monocyte count": formData.absMonocyte,
          "Absolute Basophil count": formData.absBasophil,
          "Platelet count": formData.platelet,
          Neutrophils: formData.neutrophils,
          Lymphocytes: formData.lymphocytes,
          Monocytes: formData.monocytes,
          Eosinophils: formData.eosinophils,
          Basophils: formData.basophils,
          "Mentzer Index": formData.mentzerIndex,
          "Sehgal Index": formData.sehgalIndex,
          "Platelet Hematocrit": formData.plateletHematocrit,
          "Mean Platelet Volume": formData.meanPlateletVolume,
          "Neutrophil Lymphocyte Ratio": formData.neutrophilLymphocyteRatio,
        },

        "C-Reactive Protein (CRP)": {
          "C-Reactive Protein": formData.crp,
        },

        "Diabetes Profile": {
          HbA1c: formData.hba1c,
          "Average Blood Glucose": formData.avgBloodGlucose,
        },

        "Immunoglobulin E (Total IgE)": {
          "Immunoglobulin E": formData.immunoglobulinE,
        },

        "Iron Profile": {
          Iron: formData.iron,
          TIBC: formData.tibc,
          UIBC: formData.uibc,
          "Serum Transferrin Saturation": formData.serumTransferrinSaturation,
          Ferritin: formData.ferritin,
        },

        "Lipid Profile": {
          "HDL Cholesterol": formData.hdl,
          Cholesterol: formData.cholesterol_total,
          "Triglycerides (TGL)": formData.triglycerides,
          VLDL: formData.vldl,
          "Cholesterol:HDL": formData.cholesterol_hdl,
          "LDL:HDL": formData.ldl_hdl,
          "LDL Cholesterol (Calculated)": formData.ldl_calculated,
          "Non-HDL Cholesterol": formData.non_hdl,
          "HDL/LDL ratio": formData.hdl_ldl_ratio,
        },

        "Thyroid Function Test (TFT)": {
          "Total T3": formData.t3,
          "Total T4": formData.t4,
          TSH: formData.tsh,
        },

        "Vitamin Profile": {
          "Vitamin B12": formData.vitaminB12,
          "Folic Acid": formData.folic_acid,
          "Total Vitamin D": formData.totalVitaminD,
        },

        "Liver Function Test (LFT)": {
          "Alkaline Phosphatase": formData.alkalinePhosphatase,
          "SGOT / AST - Aspartate Aminotransferase": formData.sgot_ast,
          "SGPT/ALT - Alanine Aminotransferase": formData.sgpt_alt,
          "GGT- Gamma-Glutamyl Transferase": formData.ggt,
          "Total Bilirubin": formData.totalBilirubin,
          "Direct Bilirubin": formData.directBilirubin,
          "Indirect Bilirubin": formData.indirectBilirubin,
          "Total Protein": formData.totalProtein,
          Albumin: formData.albumin,
          Globulin: formData.globulin,
          "A:G ratio": formData.ag_ratio,
          "SGOT/SGPT ratio": formData.sgot_sgpt_ratio,
        },

        "Kidney Function Test (KFT) Mini": {
          Urea: formData.urea,
          "Blood Urea Nitrogen (BUN)": formData.bun,
          Creatinine: formData.creatinine,
          Calcium: formData.calcium,
          "Uric Acid": formData.uricAcid,
          "Estimated Glomerular Filtration Rate (eGFR)": formData.egfr,
          "BUN/Creatinine ratio": formData.bun_creatinine_ratio,
        },

        // 3️⃣ FINAL S3 PDF URLs
        "Attach Official Lab Report (PDF)": uploadedUrls,
      };

      const res = await api.dataCollectionApi.post(
        "/api/forms/lab-report",
        payload,
      );

      if (sessionId) {
        await api.centerworkflowApi.post(
          `/sessions/${encodeURIComponent(
            sessionId,
          )}/center-admin/forms/LAB_REPORT_ENTRY/submit`,
          {
            reportNo: `LAB-${Date.now()}`,
            remarks: "Report entered",
          },
        );
      }

      alert("Lab Report submitted successfully!");

      // reset form data
      setFormByPatient((prev) => ({
        ...prev,
        [pid]: makeEmptyFormData(),
      }));

      // reset acknowledgement
      setAckByPatient((prev) => ({
        ...prev,
        [pid]: "No",
      }));

      // reset uploaded PDFs
      setLabReports([]);

      // reset validation errors
      setErrors({});

      sessionStorage.removeItem("workflowSessionId");
      sessionStorage.removeItem("sessionId");
      onSuccess?.();

      // window.location.href = "/new-dashboard";
    } catch (err) {
      console.error("❌ Error:", err);
      alert("Error submitting report");
    } finally {
      setLoading(false);
    }
  };

  // const renderInput = (name: string, label: string) => (
  //   <label className="lab-field">
  //     <div className="lab-label">{label}</div>

  //     <input
  //       name={name}
  //       value={formData[name] || ""}
  //       onChange={handleChange}
  //       style={{
  //         border: errors[name] ? "1px solid red" : undefined,
  //       }}
  //     />
  //   </label>
  // );

  const renderInput = (name: string, label: string, unit?: string) => (
    <label className="lab-field">
      <div className="lab-label">{label}</div>

      <div className="lab-input-with-unit">
        <input
          name={name}
          value={formData[name] || ""}
          onChange={handleChange}
          style={{
            border: errors[name] ? "1px solid red" : undefined,
          }}
        />

        {unit && <span className="lab-unit">{unit}</span>}
      </div>
    </label>
  );

  return (
    <form className="lab-form" onSubmit={handleSubmit}>
      <div className="vs-section-bar">Lab Report</div>

      {/* Acknowledgement */}
      <div className="section ack-section">
        <div className="section-header">
          <h4>Lab Report Acknowledgment</h4>
        </div>
        <div className="ack-content">
          <div className="ack-subtitle">Lab Report Received?</div>

          <div className="ack-radio-group">
            <label className="ack-radio">
              <input
                type="radio"
                name="labReportReceived"
                value="Yes"
                checked={acknowledged === "Yes"}
                onChange={() =>
                  setAckByPatient((prev) => ({ ...prev, [pid]: "Yes" }))
                }
              />
              <span className="radio-custom"></span>
              <span className="radio-label">Yes</span>
            </label>

            <label className="ack-radio">
              <input
                type="radio"
                name="labReportReceived"
                value="No"
                checked={acknowledged === "No"}
                onChange={() =>
                  setAckByPatient((prev) => ({ ...prev, [pid]: "No" }))
                }
              />
              <span className="radio-custom"></span>
              <span className="radio-label">No</span>
            </label>
          </div>
        </div>
      </div>

      {/* CBC */}
      <div className="section">
        <div className="section-header">
          <h4>Complete Blood Count (CBC)</h4>
          <button
            type="button"
            onClick={() => toggle("cbc")}
            className="collapse-btn"
          >
            {collapsed.cbc ? "Expand ▼" : "Collapse ▲"}
          </button>
        </div>
        {!collapsed.cbc && (
          <div className="section-grid">
            {/* {renderInput("hemoglobin", "Hemoglobin")}
            {renderInput("hematocritPct", "Hematocrit (%)")}
            {renderInput("rbc", "RBC Count")}
            {renderInput("wbc", "WBC Count")}
            {renderInput("platelet", "Platelet Count")}
            {renderInput("mcv", "MCV")}
            {renderInput("mch", "MCH")}
            {renderInput("mchc", "MCHC")}
            {renderInput("rdw", "Red Cell Distribution Width (RDW)")}
            {renderInput("absNeutrophil", "Absolute Neutrophil Count")}
            {renderInput("absLymphocyte", "Absolute Lymphocyte Count (ALC)")}
            {renderInput("absEosinophil", "AEC - Absolute Eosinophil Count")}
            {renderInput("absMonocyte", "Absolute Monocyte Count")}
            {renderInput("absBasophil", "Absolute Basophil Count")}
            {renderInput("neutrophils", "Neutrophils")}
            {renderInput("lymphocytes", "Lymphocytes")}
            {renderInput("monocytes", "Monocytes")}
            {renderInput("eosinophils", "Eosinophils")}
            {renderInput("basophils", "Basophils")}
            {renderInput("mentzerIndex", "Mentzer Index")}
            {renderInput("sehgalIndex", "Sehgal Index")}
            {renderInput("plateletHematocrit", "Platelet Hematocrit")}
            {renderInput("meanPlateletVolume", "Mean Platelet Volume")}
            {renderInput(
              "neutrophilLymphocyteRatio",
              "Neutrophil Lymphocyte Ratio",
            )} */}

            {renderInput("hemoglobin", "Hemoglobin", "g/dL")}
            {renderInput("hematocritPct", "Hematocrit (%)", "%")}
            {renderInput("rbc", "RBC Count", "M/µL")}
            {renderInput("wbc", "WBC Count", "/µL")}
            {renderInput("platelet", "Platelet Count", "/µL")}
            {renderInput("mcv", "MCV", "fL")}
            {renderInput("mch", "MCH", "pg")}
            {renderInput("mchc", "MCHC", "g/dL")}
            {renderInput("rdw", "Red Cell Distribution Width (RDW)", "%")}
            {renderInput("absNeutrophil", "Absolute Neutrophil Count", "cells/µ")}
            {renderInput(
              "absLymphocyte",
              "Absolute Lymphocyte Count (ALC)",
              "cells/µL",
            )}
            {renderInput(
              "absEosinophil",
              "AEC - Absolute Eosinophil Count",
              "/µL",
            )}
            {renderInput("absMonocyte", "Absolute Monocyte Count", "cells/µL")}
            {renderInput("absBasophil", "Absolute Basophil Count", "cells/µL")}
            {renderInput("neutrophils", "Neutrophils", "%")}
            {renderInput("lymphocytes", "Lymphocytes", "%")}
            {renderInput("monocytes", "Monocytes", "%")}
            {renderInput("eosinophils", "Eosinophils", "%")}
            {renderInput("basophils", "Basophils", "%")}
            {renderInput("mentzerIndex", "Mentzer Index", "")}
            {renderInput("sehgalIndex", "Sehgal Index", "")}
            {renderInput("plateletHematocrit", "Platelet Hematocrit", "%")}
            {renderInput("meanPlateletVolume", "Mean Platelet Volume", "fL")}
            {renderInput(
              "neutrophilLymphocyteRatio",
              "Neutrophil Lymphocyte Ratio",
              "",
            )}
          </div>
        )}
      </div>

      {/* CRP */}
      <div className="section">
        <div className="section-header">
          <h4>C-Reactive Protein (CRP)</h4>
          <button
            type="button"
            onClick={() => toggle("crp")}
            className="collapse-btn"
          >
            {collapsed.crp ? "Expand ▼" : "Collapse ▲"}
          </button>
        </div>
        {!collapsed.crp && (
          <div className="section-grid">
            {renderInput("crp", "C-Reactive Protein", "")}
          </div>
        )}
      </div>

      {/* Diabetes Profile */}
      <div className="section">
        <div className="section-header">
          <h4>Diabetes Profile</h4>
          <button
            type="button"
            onClick={() => toggle("diabetes")}
            className="collapse-btn"
          >
            {collapsed.diabetes ? "Expand ▼" : "Collapse ▲"}
          </button>
        </div>
        {!collapsed.diabetes && (
          <div className="section-grid">
            {renderInput("hba1c", "HbA1c", "%")}
            {renderInput("avgBloodGlucose", "Average Blood Glucose", "")}
          </div>
        )}
      </div>

      {/* Immunoglobulin E */}
      <div className="section">
        <div className="section-header">
          <h4>Immunoglobulin E (Total IgE)</h4>
          <button
            type="button"
            onClick={() => toggle("immunoglobulin")}
            className="collapse-btn"
          >
            {collapsed.immunoglobulin ? "Expand ▼" : "Collapse ▲"}
          </button>
        </div>
        {!collapsed.immunoglobulin && (
          <div className="section-grid">
            {renderInput("immunoglobulinE", "Immunoglobulin E", "IU/mL")}
          </div>
        )}
      </div>

      {/* Iron Profile */}
      <div className="section">
        <div className="section-header">
          <h4>Iron Profile</h4>
          <button
            type="button"
            onClick={() => toggle("iron")}
            className="collapse-btn"
          >
            {collapsed.iron ? "Expand ▼" : "Collapse ▲"}
          </button>
        </div>
        {!collapsed.iron && (
          <div className="section-grid">
            {renderInput("iron", "Iron", "µg/dL")}
            {renderInput("tibc", "TIBC", "µg/dL")}
            {renderInput("uibc", "UIBC", "µg/dL")}
            {renderInput(
              "serumTransferrinSaturation",
              "Serum Transferrin Saturation",
              "%",
            )}
            {renderInput("ferritin", "Ferritin", "ng/mL")}
          </div>
        )}
      </div>

      {/* Lipid Profile */}
      <div className="section">
        <div className="section-header">
          <h4>Lipid Profile</h4>
          <button
            type="button"
            onClick={() => toggle("lipid")}
            className="collapse-btn"
          >
            {collapsed.lipid ? "Expand ▼" : "Collapse ▲"}
          </button>
        </div>
        {!collapsed.lipid && (
          <div className="section-grid">
            {renderInput("hdlCholesterol", "HDL Cholesterol", "mg/dL")}
            {renderInput("cholesterol", "Cholesterol", "mg/dL")}
            {renderInput("triglycerides", "Triglycerides (TGL)", "mg/dL")}
            {renderInput("vldl", "VLDL", "mg/dL")}
            {renderInput("cholesterol_hdl", "Cholesterol:HDL", "")}
            {renderInput("ldl_hdl", "LDL:HDL", "")}
            {renderInput(
              "ldlCalculated",
              "LDL Cholesterol (Calculated)",
              "mg/dL",
            )}
            {renderInput("non_hdlCholesterol", "Non-HDL Cholesterol", "mg/dL")}
            {renderInput("hdl_ldl_ratio", "HDL/LDL Ratio", "")}
          </div>
        )}
      </div>

      {/* LFT */}
      <div className="section">
        <div className="section-header">
          <h4>Liver Function Test (LFT)</h4>
          <button
            type="button"
            onClick={() => toggle("liver")}
            className="collapse-btn"
          >
            {collapsed.liver ? "Expand ▼" : "Collapse ▲"}
          </button>
        </div>
        {!collapsed.liver && (
          <div className="section-grid">
            {renderInput("alkalinePhosphatase", "Alkaline Phosphatase", "U/L")}
            {renderInput("sgot_ast", "SGOT / AST", "U/L")}
            {renderInput("sgpt_alt", "SGPT / ALT", "U/L")}
            {renderInput("ggt", "GGT", "U/L")}
            {renderInput("totalBilirubin", "Total Bilirubin", "mg/dL")}
            {renderInput("directBilirubin", "Direct Bilirubin", "mg/dL")}
            {renderInput("indirectBilirubin", "Indirect Bilirubin", "mg/dL")}
            {renderInput("totalProtein", "Total Protein", "g/dL")}
            {renderInput("albumin", "Albumin", "g/dL")}
            {renderInput("globulin", "Globulin", "g/dL")}
            {renderInput("ag_ratio", "A:G Ratio", "")}
            {renderInput("sgot_sgpt_ratio", "SGOT/SGPT Ratio", "")}
          </div>
        )}
      </div>

      {/* Thyroid */}
      <div className="section">
        <div className="section-header">
          <h4>Thyroid Function Test (TFT)</h4>
          <button
            type="button"
            onClick={() => toggle("thyroid")}
            className="collapse-btn"
          >
            {collapsed.thyroid ? "Expand ▼" : "Collapse ▲"}
          </button>
        </div>
        {!collapsed.thyroid && (
          <div className="section-grid">
            {renderInput("totalT3", "Total T3", "")}
            {renderInput("totalT4", "Total T4", "")}
            {renderInput("tsh", "TSH", "mIU/L")}
          </div>
        )}
      </div>

      {/* Vitamins */}
      <div className="section">
        <div className="section-header">
          <h4>Vitamin Profile</h4>
          <button
            type="button"
            onClick={() => toggle("vitamins")}
            className="collapse-btn"
          >
            {collapsed.vitamins ? "Expand ▼" : "Collapse ▲"}
          </button>
        </div>
        {!collapsed.vitamins && (
          <div className="section-grid">
            {renderInput("vitaminB12", "Vitamin B12", "pg/mL")}
            {renderInput("folicAcid", "Folic Acid", "ng/mL")}
            {renderInput("totalVitaminD", "Total Vitamin D", "ng/mL")}
          </div>
        )}
      </div>

      {/* Kidney */}
      <div className="section">
        <div className="section-header">
          <h4>Kidney Function Test (KFT Mini)</h4>
          <button
            type="button"
            onClick={() => toggle("kidney")}
            className="collapse-btn"
          >
            {collapsed.kidney ? "Expand ▼" : "Collapse ▲"}
          </button>
        </div>
        {!collapsed.kidney && (
          <div className="section-grid">
            {renderInput("urea", "Urea", "mg/dL")}
            {renderInput("bun", "Blood Urea Nitrogen (BUN)", "mg/dL")}
            {renderInput("creatinine", "Creatinine", "mg/dL")}
            {renderInput("calcium", "Calcium", "mg/dL")}
            {renderInput("uricAcid", "Uric Acid", "mg/dL")}
            {renderInput("egfr", "Estimated GFR (eGFR)", "mL/min/1.73m²")}
            {renderInput("bun_creatinine_ratio", "BUN/Creatinine Ratio", "")}
          </div>
        )}
      </div>

      {/* PDF Upload Section */}
      <div className="section pdf-section">
        <div className="section-header">
          <h4>Attach Official Lab Report (PDF)</h4>
        </div>

        <div
          className="pdf-upload-box"
          onClick={() => document.getElementById("labPdfInput")?.click()}
        >
          <div className="pdf-upload-inner">
            <div className="cloud-icon">☁️</div>
            <div className="upload-text">
              Drag and drop your PDF or click to browse
            </div>

            <label className="choose-btn">Choose File</label>

            <input
              id="labPdfInput"
              type="file"
              accept="application/pdf"
              multiple
              hidden
              onChange={handleFilePick}
            />

            <div className="max-size-text">Max file size: 10MB (PDF only)</div>
          </div>
        </div>

        {/* File list */}
        {labReports.map((file, i) => (
          <div key={i} className="uploaded-row">
            <div className="file-left">
              <span className="file-icon">📄</span>
              <span className="file-name">{file.name}</span>
              <span className="file-size">
                • {(file.size / 1024 / 1024).toFixed(1)} MB
              </span>
            </div>

            <button
              type="button"
              className="remove-file-btn"
              onClick={() => handleRemoveFile(i)}
            >
              ✖
            </button>
          </div>
        ))}
      </div>

      <div className="audio-actions">
        <button type="button" className="audio-btn audio-btn-secondary">
          Close
        </button>

        <button
          type="submit"
          className="audio-btn audio-btn-primary"
          disabled={loading || s3Uploading}
        >
          {loading || s3Uploading ? "Submitting..." : "Submit Report"}
        </button>
      </div>
    </form>
  );
};

export default LabReportForm;
